/**
 * AttendanceReportPanel
 *
 * Surfaces GET /api/calendar/attendance-report — per-promotora attendance
 * across Hub Meetings and Training/Workshops. Server-side gated to
 * supervisor+ (frontline-staff callers receive 403 and see the empty-
 * state below instead of a crash).
 *
 * Three actions:
 * - View rows in a compact table
 * - Download as CSV (server-side stream)
 * - Download as XLSX (client-side via downloadXLSX helper from PR #14)
 */

import React, { useEffect, useState } from 'react';
import { Calendar, Download, FileSpreadsheet, AlertTriangle, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  AttendanceReportRow,
  downloadAttendanceCsv,
  getAttendanceReport,
} from '@/services/api/outreachActivities';
import { downloadXLSX } from '@/utils/exportUtils';

export const AttendanceReportPanel: React.FC = () => {
  const [rows, setRows] = useState<AttendanceReportRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [startDate, setStartDate] = useState<string>(() => firstOfQuarter());
  const [endDate, setEndDate] = useState<string>(() => today());
  const [typeFilter, setTypeFilter] = useState<'network_hub_meeting' | 'training_workshop' | ''>('');

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    setForbidden(false);
    getAttendanceReport({
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      event_type: typeFilter || undefined,
    })
      .then((data) => {
        if (!cancelled) setRows(data.rows);
      })
      .catch((e: any) => {
        if (cancelled) return;
        const status = e?.status ?? e?.response?.status;
        if (status === 403) {
          setForbidden(true);
        } else {
          setError(e?.message || 'Failed to load attendance report.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, typeFilter]);

  const onXlsx = () => {
    if (!rows || rows.length === 0) return;
    downloadXLSX(rows as any[], 'attendance-report', 'Attendance').catch((e) =>
      setError(e?.message || 'XLSX download failed.'),
    );
  };
  const onCsv = () => {
    downloadAttendanceCsv({
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      event_type: typeFilter || undefined,
    }).catch((e) => setError(e?.message || 'CSV download failed.'));
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start gap-2 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              Per-Staff Attendance
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Who attended which Hub Meeting / Training in the selected window. Supervisor and above
              only.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onCsv}
              disabled={forbidden || !rows || rows.length === 0}
            >
              <Download className="w-4 h-4 mr-1" />
              CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onXlsx}
              disabled={forbidden || !rows || rows.length === 0}
            >
              <FileSpreadsheet className="w-4 h-4 mr-1" />
              XLSX
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1 flex items-center gap-1">
              <Filter className="w-3 h-3" />
              Type
            </label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as any)}
              className="block w-full border border-gray-300 dark:border-gray-700 dark:bg-gray-900 rounded-md px-3 py-2 text-sm"
            >
              <option value="">Both</option>
              <option value="network_hub_meeting">Hub meetings</option>
              <option value="training_workshop">Trainings &amp; workshops</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">From</label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">To</label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </div>

        {forbidden && (
          <div className="flex gap-2 items-start text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-3">
            <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-700" />
            <span>
              Per-staff attendance is available to supervisor-tier roles only. Your aggregate
              participation is visible from <strong>Hub Meetings &amp; Trainings</strong> instead.
            </span>
          </div>
        )}
        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-2">{error}</div>
        )}

        {!forbidden && rows && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Event</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Staff member</th>
                  <th className="px-3 py-2 text-right">Duration</th>
                  <th className="px-3 py-2 text-left">Response</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-gray-500">
                      No attendance records in this range.
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => (
                    <tr key={`${r.event_id}-${r.user_id}-${idx}`} className="border-t border-gray-100 dark:border-gray-700">
                      <td className="px-3 py-1.5">{r.event_date ? r.event_date.slice(0, 10) : '—'}</td>
                      <td className="px-3 py-1.5 truncate max-w-xs">{r.event_title}</td>
                      <td className="px-3 py-1.5 text-xs text-gray-600">
                        {r.event_type === 'network_hub_meeting' ? 'Hub' : 'Training'}
                      </td>
                      <td className="px-3 py-1.5">{r.promotora_name || r.promotora_email || `user #${r.user_id}`}</td>
                      <td className="px-3 py-1.5 text-right">{r.duration_minutes ?? '—'} min</td>
                      <td className="px-3 py-1.5 text-xs">{r.response_status || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        {!forbidden && rows && rows.length > 0 && (
          <div className="text-xs text-gray-500">{rows.length} attendance record(s)</div>
        )}
      </CardContent>
    </Card>
  );
};

function today(): string {
  return new Date().toISOString().slice(0, 10);
}
function firstOfQuarter(): string {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3);
  return new Date(now.getFullYear(), q * 3, 1).toISOString().slice(0, 10);
}

export default AttendanceReportPanel;
